import { useEffect, useState } from "react";
import { Button, Drawer, DrawerBody, DrawerFooter, DrawerHeader, Input, Select } from "@/components";
import type { ApiDeviceBranding } from "../../_types/device-brandings.types";
import { BRANDING_PROTOTYPE_ASSET_OPTIONS } from "../../_constants/device-branding-prototypes.const";
import styles from "../create-brand-drawer/CreateBrandDrawer.module.scss";

type EditBrandDrawerProps = {
  isOpen: boolean;
  brand: ApiDeviceBranding | null;
  onClose: () => void;
  onSave: (brand: ApiDeviceBranding) => void;
};

const NONE = "";

export const EditBrandDrawer = ({ isOpen, brand, onClose, onSave }: EditBrandDrawerProps) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [accentColor, setAccentColor] = useState("");
  const [wallpaperAssetId, setWallpaperAssetId] = useState(NONE);
  const [logoAssetId, setLogoAssetId] = useState(NONE);
  const [alertSoundAssetId, setAlertSoundAssetId] = useState(NONE);
  const [nameError, setNameError] = useState("");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen || !brand) return;
    setName(brand.name);
    setDescription(brand.description ?? "");
    setDisplayName(brand.displayName ?? "");
    setAccentColor(brand.accentColor ?? "");
    setWallpaperAssetId(brand.wallpaperAssetId ?? NONE);
    setLogoAssetId(brand.logoAssetId ?? NONE);
    setAlertSoundAssetId(brand.alertSoundAssetId ?? NONE);
    setNameError("");
    setSubmitError(null);
  }, [isOpen, brand]);

  const handleClose = () => {
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!brand) return;
    if (name.trim().length < 1) {
      setNameError("Name is required");
      return;
    }
    setNameError("");
    setSubmitError(null);
    const accent = accentColor.trim();
    const accentOk =
      accent === "" || /^#([0-9A-Fa-f]{6}|[0-9A-Fa-f]{8})$/.test(accent);
    if (!accentOk) {
      setSubmitError("Accent color must be empty or a hex like #RRGGBB or #RRGGBBAA.");
      return;
    }
    onSave({
      ...brand,
      name: name.trim(),
      description: description.trim() || null,
      displayName: displayName.trim() || null,
      accentColor: accent || null,
      wallpaperAssetId: wallpaperAssetId || null,
      logoAssetId: logoAssetId || null,
      alertSoundAssetId: alertSoundAssetId || null,
      extra: { ...(brand.extra ?? {}), prototype: true },
      configVersion: brand.configVersion + 1,
      updatedAt: Date.now(),
    });
    onClose();
  };

  const imageAssets = BRANDING_PROTOTYPE_ASSET_OPTIONS.filter((a) => a.kind === "image");
  const audioAssets = BRANDING_PROTOTYPE_ASSET_OPTIONS.filter((a) => a.kind === "audio");

  return (
    <Drawer isOpen={isOpen} onClose={handleClose} ariaLabel="Edit branding (prototype)">
      <DrawerHeader title="Edit branding (Prototype)" onClose={handleClose} />
      <form className={styles["comp__stack"]} onSubmit={handleSubmit}>
        <DrawerBody>
          <div className={styles["comp"]}>
            <p className={styles["comp__hint"]}>
              Prototype / Preview — edits stay in memory until refresh and never call the API.
            </p>
            <div className={styles["comp__fields"]}>
              <Input
                id="edit-brand-name"
                label="Name"
                value={name}
                placeholder="e.g. Vetty Kiosk — default"
                required
                error={nameError}
                onChange={(v) => {
                  setName(v);
                  setNameError("");
                  setSubmitError(null);
                }}
              />

              <Input
                id="edit-brand-description"
                label="Description"
                value={description}
                placeholder="Optional"
                onChange={(v) => setDescription(v)}
              />

              <Input
                id="edit-brand-display-name"
                label="Display name on device"
                value={displayName}
                placeholder="Optional"
                onChange={(v) => setDisplayName(v)}
              />

              <Input
                id="edit-brand-accent"
                label="Accent color (hex)"
                value={accentColor}
                placeholder="#ff541f"
                onChange={(v) => setAccentColor(v)}
              />

              <Select
                id="edit-brand-wallpaper"
                label="Wallpaper (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={wallpaperAssetId}
                onChange={(v) => setWallpaperAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {imageAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>

              <Select
                id="edit-brand-logo"
                label="Logo (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={logoAssetId}
                onChange={(v) => setLogoAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {imageAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>

              <Select
                id="edit-brand-alert"
                label="Alert sound (sample)"
                fullWidth
                size="medium"
                variant="secondary"
                value={alertSoundAssetId}
                onChange={(v) => setAlertSoundAssetId(String(v))}
              >
                <option value={NONE}>None</option>
                {audioAssets.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.label}
                  </option>
                ))}
              </Select>
            </div>

            {submitError ? <div className={styles["comp__error"]}>{submitError}</div> : null}
          </div>
        </DrawerBody>
        <DrawerFooter>
          <Button type="button" variant="secondary" onClick={handleClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary">
            Save changes
          </Button>
        </DrawerFooter>
      </form>
    </Drawer>
  );
};
